/**
 * Website forms, built in Admin and embedded on Otii's website with one
 * script tag. A demo form books a live slot in the hello@ calendar first,
 * then takes the person's details; an enquiry form is the details alone.
 * Every submission files a lead: provider, contact, the deal at the form's
 * stage, and the submission on the timeline.
 */
import { z } from "zod";

/** Typed fields map onto the record; the rest are kept as answers on the timeline. */
export type FormFieldType =
  | "first_name"
  | "last_name"
  | "email"
  | "phone"
  | "organisation"
  | "sites"
  | "job_title"
  | "town"
  | "message"
  | "text"
  | "textarea"
  | "select"
  | "checkbox"
  | "consent";

export const FORM_FIELD_TYPES: { type: FormFieldType; label: string; once: boolean }[] = [
  { type: "first_name", label: "First name", once: true },
  { type: "last_name", label: "Last name", once: true },
  { type: "email", label: "Email", once: true },
  { type: "phone", label: "Phone", once: true },
  { type: "organisation", label: "Nursery or group", once: true },
  { type: "sites", label: "Number of sites", once: true },
  { type: "job_title", label: "Job title", once: true },
  { type: "town", label: "Town", once: true },
  { type: "message", label: "Message", once: true },
  { type: "text", label: "Short answer", once: false },
  { type: "textarea", label: "Long answer", once: false },
  { type: "select", label: "Choice", once: false },
  { type: "checkbox", label: "Tick box", once: false },
  { type: "consent", label: "Marketing consent", once: true },
];

export interface FormField {
  id: string;
  type: FormFieldType;
  label: string;
  required: boolean;
  placeholder: string | null;
  /** Choices, for a select. */
  options: string[];
  /** Half-width fields sit in pairs on wider screens. */
  half: boolean;
}

export interface FormSettings {
  /** A demo form books a slot in the hello@ calendar before the details. */
  kind: "enquiry" | "demo";
  title: string;
  intro: string;
  buttonText: string;
  thankYou: string;
  /** The stage the deal starts in; null files the person without a deal. */
  stageId: string | null;
  leadSourceId: string | null;
  ownerId: string | null;
  /** Topics a ticked consent box signs them up to. */
  topicIds: string[];
}

export interface WebForm {
  id: string;
  name: string;
  key: string;
  active: boolean;
  fields: FormField[];
  settings: FormSettings;
  views: number;
  submissions: number;
  lastSubmissionAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** What the website gets: no ids of people or stages. */
export interface PublicForm {
  key: string;
  kind: "enquiry" | "demo";
  title: string;
  intro: string;
  buttonText: string;
  thankYou: string;
  fields: FormField[];
  booking: { durationMinutes: number; timezone: string } | null;
}

export interface FormSubmission {
  id: string;
  formId: string;
  createdAt: string;
  outcome: "filed" | "spam" | "error";
  note: string | null;
  values: Record<string, string | boolean>;
  bookingStart: string | null;
  organisationId: string | null;
  organisationName: string | null;
  contactId: string | null;
  dealId: string | null;
}

export interface BookingSettings {
  title: string;
  description: string;
  durationMinutes: number;
  /** ISO weekdays: 1 Monday … 7 Sunday. */
  days: number[];
  /** "10:00": the first start; the last ends by dayEnd. */
  dayStart: string;
  dayEnd: string;
  /** Minutes between possible starts. */
  intervalMinutes: number;
  minNoticeHours: number;
  maxDaysAhead: number;
  /** Kept free before and after other meetings. */
  bufferMinutes: number;
  timezone: string;
}

export const DEFAULT_BOOKING_SETTINGS: BookingSettings = {
  title: "Otii Discovery Call",
  description:
    "A calm, practical conversation about how your setting is operating and how visible that practice really is.\n\nDuring the call, we'll:\n– Reflect your current practice against Ofsted's evaluation areas\n– Identify where structure or visibility could be stronger\n– Explore how Otii makes strong daily practice easier to evidence\n\nNo pressure. No performance. Just clarity.",
  durationMinutes: 45,
  days: [1, 2, 3, 4, 5],
  dayStart: "10:00",
  dayEnd: "17:00",
  intervalMinutes: 60,
  minNoticeHours: 48,
  maxDaysAhead: 18,
  bufferMinutes: 0,
  timezone: "Europe/London",
};

export interface BookingStatus {
  mode: "demo" | "live" | "off";
  connection: { email: string; status: "connected" | "error"; lastError: string | null; connectedAt: string } | null;
}

export interface BookingSlot {
  start: string;
  end: string;
}

const field = (id: string, type: FormFieldType, label: string, required: boolean, half = false): FormField => ({ id, type, label, required, placeholder: null, options: [], half });

/** Starting points for a new form. */
export const FORM_TEMPLATES: Record<"demo" | "enquiry", { name: string; fields: FormField[]; settings: Omit<FormSettings, "ownerId" | "topicIds"> }> = {
  demo: {
    name: "Book a demo",
    fields: [
      field("first_name", "first_name", "First name", true, true),
      field("last_name", "last_name", "Last name", true, true),
      field("email", "email", "Work email", true, true),
      field("phone", "phone", "Phone", false, true),
      field("organisation", "organisation", "Nursery or group", true, true),
      field("sites", "sites", "Number of sites", true, true),
      field("message", "message", "Anything we should know?", false),
      field("consent", "consent", "Send me Otii news and tips", false),
    ],
    settings: {
      kind: "demo",
      title: "Book your Otii demo",
      intro: "",
      buttonText: "Book demo",
      thankYou: "You're booked in. An invite with the Google Meet link is on its way.",
      stageId: "st-demo-booked",
      leadSourceId: "src-web",
    },
  },
  enquiry: {
    name: "General enquiry",
    fields: [
      field("first_name", "first_name", "First name", true, true),
      field("last_name", "last_name", "Last name", true, true),
      field("email", "email", "Email", true, true),
      field("organisation", "organisation", "Nursery or group", false, true),
      field("message", "message", "How can we help?", true),
      field("consent", "consent", "Send me Otii news and tips", false),
    ],
    settings: {
      kind: "enquiry",
      title: "Get in touch",
      intro: "",
      buttonText: "Send",
      thankYou: "Thanks, we'll be in touch shortly.",
      stageId: "st-new",
      leadSourceId: "src-web",
    },
  },
};

const fieldInput = z.object({
  id: z.string().trim().min(1).max(60),
  type: z.enum(["first_name", "last_name", "email", "phone", "organisation", "sites", "job_title", "town", "message", "text", "textarea", "select", "checkbox", "consent"]),
  label: z.string().trim().min(1, "Give the field a label").max(300),
  required: z.boolean(),
  placeholder: z.string().trim().max(200).nullish().transform((v) => v || null),
  options: z.array(z.string().trim().min(1).max(200)).max(50).default([]),
  half: z.boolean().default(false),
});

export const formInput = z
  .object({
    name: z.string().trim().min(1, "Name the form").max(120),
    active: z.boolean().default(true),
    fields: z.array(fieldInput).min(1, "Add at least one field").max(40),
    settings: z.object({
      kind: z.enum(["enquiry", "demo"]),
      title: z.string().trim().max(200).default(""),
      intro: z.string().trim().max(2000).default(""),
      buttonText: z.string().trim().min(1, "Say what the button says").max(60),
      thankYou: z.string().trim().min(1, "Write the thank-you message").max(1000),
      stageId: z.string().min(1).nullable(),
      leadSourceId: z.string().min(1).nullable(),
      ownerId: z.string().min(1).nullable(),
      topicIds: z.array(z.string()).default([]),
    }),
  })
  .superRefine((f, ctx) => {
    if (!f.fields.some((x) => x.type === "email")) ctx.addIssue({ code: "custom", path: ["fields"], message: "Every form needs an Email field: it's how the person is matched and replied to." });
    const seen = new Set<string>();
    f.fields.forEach((x, i) => {
      if (seen.has(x.id)) ctx.addIssue({ code: "custom", path: ["fields", i, "id"], message: "Each field needs its own id." });
      seen.add(x.id);
      if (x.type === "select" && !x.options.length) ctx.addIssue({ code: "custom", path: ["fields", i, "options"], message: "Give the choice some options." });
      if (FORM_FIELD_TYPES.find((t) => t.type === x.type)?.once && f.fields.filter((y) => y.type === x.type).length > 1)
        ctx.addIssue({ code: "custom", path: ["fields", i, "type"], message: "That kind of field can only appear once." });
    });
  });
export type FormInput = z.input<typeof formInput>;

export const bookingSettingsInput = z
  .object({
    title: z.string().trim().min(1, "Name the meeting").max(200),
    description: z.string().trim().max(4000),
    durationMinutes: z.coerce.number().int().min(10).max(240),
    days: z.array(z.number().int().min(1).max(7)).min(1, "Pick at least one day"),
    dayStart: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use HH:MM"),
    dayEnd: z.string().regex(/^([01]\d|2[0-4]):[0-5]\d$/, "Use HH:MM"),
    intervalMinutes: z.coerce.number().int().min(5).max(240),
    minNoticeHours: z.coerce.number().int().min(0).max(24 * 30),
    maxDaysAhead: z.coerce.number().int().min(1).max(365),
    bufferMinutes: z.coerce.number().int().min(0).max(120),
    timezone: z.string().min(1).default("Europe/London"),
  })
  .refine((b) => b.dayEnd > b.dayStart, { path: ["dayEnd"], message: "The day ends after it starts" });

/** What the website posts. `hp` is a field people never see; `elapsed` is how long the form was open. */
export const formSubmitInput = z.object({
  values: z.record(z.string(), z.union([z.string().max(5000), z.boolean()])),
  slot: z.string().datetime().nullish(),
  hp: z.string().max(500).default(""),
  /** The tracked link that brought them, from utm_id on the landing address: a slug, nothing personal. */
  link: z.string().trim().max(80).nullish().transform((v) => v || null),
  elapsed: z.number().nonnegative().default(0),
});
